import { useState } from 'react';
import { useDashboard } from '../../hooks/useDashboard';
import { useCache } from '../../hooks/useCache';
import { fetchSOP, mediaUrl } from '../../api';
import ImageLightbox from '../ui/ImageLightbox';
import VideoLightbox from '../ui/VideoLightbox';
import { Skeleton, ChartSkeleton } from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import styles from './SopCompliance.module.css';
import kpiStyles from '../ui/TabKpiRow.module.css';

function KpiCard({ label, value, unit }) {
  return (
    <div className={kpiStyles.card}>
      <div className={kpiStyles.lbl}>{label}</div>
      <div className={kpiStyles.val}>{value}{unit && <span className={kpiStyles.unit}> {unit}</span>}</div>
    </div>
  );
}

function BreachCard({ breach, type, onImageClick }) {
  const isHygiene = type === 'hygiene';
  const rawUrl = mediaUrl(breach.imageUrl);
  const imageUrl = rawUrl ? `${rawUrl}${rawUrl.includes('?') ? '&' : '?'}_cb=${breach.id || Math.random()}` : null;
  const [imgError, setImgError] = useState(false);

  return (
    <div className={styles.breachCard}>
      <div className={styles.breachFrame} onClick={() => imageUrl && onImageClick()} style={{ cursor: imageUrl ? 'zoom-in' : 'default' }}>
        {imageUrl && !imgError ? (
          <img
            src={imageUrl}
            alt={isHygiene ? breach.reason : breach.issue}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onError={() => setImgError(true)}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>
            No image
          </div>
        )}
      </div>
      <div className={styles.breachInfo}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div className={styles.breachStore}>{breach.date} · {breach.timestamp}</div>
          {isHygiene && breach.dirtyDurationMin != null && (
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--red)', whiteSpace: 'nowrap' }}>
              Time taken to clean - {Math.round(breach.dirtyDurationMin)} min
            </div>
          )}
        </div>
        {!isHygiene && (
          <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
            {breach.gloves === false && <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: 'var(--red-bg)', color: 'var(--red)' }}>No Gloves</span>}
            {breach.hairnet === false && <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: 'var(--red-bg)', color: 'var(--red)' }}>No Hairnet</span>}
            {breach.mask === false && <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: 'var(--red-bg)', color: 'var(--red)' }}>No Mask</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function VideoCard({ rec, onPlay }) {
  const videoUrl = mediaUrl(rec.videoUrl);
  const posterUrl = mediaUrl(rec.thumbnailUrl) || undefined;
  const [imgError, setImgError] = useState(false);

  return (
    <div className={styles.breachCard}>
      <div
        className={styles.breachFrame}
        onClick={() => videoUrl && onPlay()}
        style={{ cursor: videoUrl ? 'pointer' : 'default', background: '#000', position: 'relative' }}
      >
        {posterUrl && !imgError ? (
          <img
            src={posterUrl}
            alt="cash counting"
            loading="lazy"
            onError={() => setImgError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>
            {videoUrl ? '' : 'No video'}
          </div>
        )}
        {/* Play overlay */}
        {videoUrl && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
            </div>
          </div>
        )}
      </div>
      <div className={styles.breachInfo}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div className={styles.breachStore}>{rec.date} · {rec.timestamp}</div>
          {rec.durationSec != null && (
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--g500)', whiteSpace: 'nowrap' }}>
              {Math.round(rec.durationSec)}s
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SopCompliance() {
  const { currentStore, exitStore, startDate, endDate } = useDashboard();
  const [activeTab, setActiveTab] = useState('hygiene');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [videoIndex, setVideoIndex] = useState(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  // Server-side pagination — page is part of the cache key
  const cacheKey = currentStore ? `sop:${currentStore.storeId}:${startDate}:${endDate}:p${page}` : null;
  const { data: sopData, loading } = useCache(cacheKey, () => fetchSOP(currentStore.storeId, startDate, endDate, page, PER_PAGE));

  if (!currentStore) return null;

  if (loading) {
    return (
      <div className="section">
        <div className="sec-hdr">
          <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--g900)' }}>SOP Compliance</h2>
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={70} radius={10} />)}
        </div>
        <ChartSkeleton height={260} />
      </div>
    );
  }

  const hygiene = sopData?.hygiene || { breaches: [], totalBreaches: 0, totalChecks: 0, complianceRate: 100 };
  const ppe = sopData?.ppe || { breaches: [], totalNonCompliant: 0, totalChecks: 0, complianceRate: 100 };
  const cash = sopData?.cashCounting || { records: [], total: 0 };
  const totalBreaches = hygiene.totalBreaches + ppe.totalNonCompliant;
  const isCash = activeTab === 'cash';
  const currentBreaches = activeTab === 'hygiene' ? hygiene.breaches : activeTab === 'ppe' ? ppe.breaches : cash.records;
  const totalCount = activeTab === 'hygiene' ? hygiene.totalBreaches : activeTab === 'ppe' ? ppe.totalNonCompliant : cash.total;

  const ppeChecks = ppe.totalChecks || 0;
  const hairnetRate = ppeChecks > 0 ? Math.round(((ppeChecks - (ppe.noHairnet || 0)) / ppeChecks) * 100) : 0;
  const glovesRate  = ppeChecks > 0 ? Math.round(((ppeChecks - (ppe.noGloves  || 0)) / ppeChecks) * 100) : 0;

  // Build image URL list for lightbox navigation
  function resolveBreachUrl(b) {
    const raw = mediaUrl(b.imageUrl);
    return raw ? `${raw}${raw.includes('?') ? '&' : '?'}_cb=${b.id || Math.random()}` : null;
  }
  const imageUrls = currentBreaches.map(resolveBreachUrl).filter(Boolean);

  // Resolve cash-counting clips for the video popup
  const cashVideos = cash.records.map(r => ({
    videoUrl: mediaUrl(r.videoUrl),
    posterUrl: mediaUrl(r.thumbnailUrl) || undefined,
    date: r.date,
    timestamp: r.timestamp,
  }));

  return (
    <div className="section">
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
        <button onClick={exitStore} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 500, color: 'var(--blue)', cursor: 'pointer', background: 'none', border: 'none', fontFamily: 'var(--font)', padding: '5px 10px', borderRadius: 7 }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8 2L4 6l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          All Stores
        </button>
        <span style={{ color: 'var(--g300)', fontSize: 11 }}>/</span>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--g900)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: currentStore.color }} />
          A2B {currentStore.name}
        </div>
      </div>

      {/* Dynamic KPI row — swaps with active sidebar tab */}
      <div className={kpiStyles.row} style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
        {activeTab === 'hygiene' ? (
          <>
            <KpiCard label="Total Hygiene Breaches" value={hygiene.totalBreaches} />
            <KpiCard label="Average Cleaning Time" value={Math.round(hygiene.avgDirtyDurationMin || 0)} unit="min" />
            <KpiCard
              label="Most Cluttered Table"
              value={hygiene.mostClutteredZoneId != null ? `Table ${hygiene.mostClutteredZoneId}` : '—'}
            />
          </>
        ) : activeTab === 'cash' ? (
          <KpiCard label="Total" value={cash.total || 0} />
        ) : (
          <>
            <KpiCard label="Total PPE Compliance" value={ppe.totalNonCompliant || 0} />
            <KpiCard label="Hairnet Compliance Rate" value={hairnetRate} unit="%" />
            <KpiCard label="Gloves Compliance Rate" value={glovesRate} unit="%" />
          </>
        )}
      </div>

      <div className="sec-hdr">
        <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--g900)' }}>SOP Compliance</h2>
      </div>

      <div className={styles.panel}>
        {/* Vertical sidebar tabs */}
        <div className={styles.sidebar}>
          {[
            {
              key: 'hygiene',
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12V8.5a1.5 1.5 0 0 1 3 0V12"/><path d="M4 10h3"/>
                  <path d="M7 12V7a1.5 1.5 0 0 1 3 0v5"/><path d="M10 9.5V6.5a1.5 1.5 0 0 1 3 0V12"/>
                  <path d="M13 10.5V9a1.5 1.5 0 0 1 3 0v3.5a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5v-1"/>
                  <path d="M7 19.5V21M12 19.5V21M17 19.5V21"/>
                  <line x1="5" y1="3" x2="5" y2="5"/><line x1="9" y1="2" x2="9" y2="4"/><line x1="13" y1="3" x2="13" y2="5"/>
                </svg>
              ),
              label: 'Hygiene Compliance',
              sub: `${hygiene.complianceRate}% compliance`,
              count: hygiene.totalBreaches,
            },
            {
              key: 'ppe',
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 18a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v2z"/>
                  <path d="M10 10V8a2 2 0 1 1 4 0v2"/><path d="M6 15V9a6 6 0 0 1 12 0v6"/>
                </svg>
              ),
              label: 'PPE Compliance',
              sub: `${ppe.complianceRate}% compliance`,
              count: ppe.totalNonCompliant,
            },
            {
              key: 'cash',
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="6" width="20" height="12" rx="2"/>
                  <circle cx="12" cy="12" r="2.5"/>
                  <path d="M5 9v6M19 9v6"/>
                </svg>
              ),
              label: 'Cash Counting',
              sub: `${cash.total} records`,
              count: cash.total,
            },
          ].map(t => (
            <button key={t.key} className={`${styles.vtab} ${activeTab === t.key ? styles.vtabActive : ''}`} onClick={() => { setActiveTab(t.key); setPage(1); }}>
              <span className={styles.vtabIcon}>{t.icon}</span>
              <div className={styles.vtabText}>
                <span className={styles.vtabLabel}>{t.label}</span>
                <span className={styles.vtabSub}>{t.sub}</span>
              </div>
              <span className={`${styles.vtabCount} ${activeTab === t.key ? styles.vtabCountActive : ''}`}>{t.count}</span>
            </button>
          ))}
        </div>

        {/* Right — breach images */}
        <div className={styles.right}>
          <div className={styles.rightHdr}>
            <div>
              <div className={styles.rightTitle}>{isCash ? 'Cash Counting Evidence' : 'Breach Camera Evidence'}</div>
              <div className={styles.rightSub}>
                {activeTab === 'hygiene' ? 'Hygiene violations detected by AI' : activeTab === 'ppe' ? 'PPE violations detected by AI' : 'Cash counting events detected by AI'}
              </div>
            </div>
            <span className="count-badge">
              {totalCount} {isCash ? 'records' : 'breaches'}
            </span>
          </div>
          {currentBreaches.length === 0 && !loading ? (
            <EmptyState message={isCash ? 'No records found' : 'No breaches detected'} compact />
          ) : (() => {
            const totalPages = Math.ceil(totalCount / PER_PAGE);
            return (
              <>
                <div className={styles.imgGrid} key={`${activeTab}-${page}`}>
                  {currentBreaches.map((b, i) => {
                    const globalIdx = (page - 1) * PER_PAGE + i;
                    if (isCash) {
                      return <VideoCard key={`cash-${b.id || globalIdx}`} rec={b} onPlay={() => setVideoIndex(i)} />;
                    }
                    return <BreachCard key={`${activeTab}-${b.id || globalIdx}`} breach={b} type={activeTab} idx={globalIdx} onImageClick={() => setLightboxIndex(i)} />;
                  })}
                </div>
                {totalPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px' }}>
                    <span style={{ fontSize: 11, color: 'var(--g400)' }}>
                      Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, totalCount)} of {totalCount}
                    </span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ minWidth: 28, height: 28, borderRadius: 6, background: 'var(--white)', border: '1px solid var(--g200)', cursor: page === 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: page === 1 ? 0.35 : 1 }}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8 2L4 6l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                      <button disabled={page === 1} onClick={() => setPage(1)} style={{ minWidth: 28, height: 28, borderRadius: 6, background: 'var(--white)', border: '1px solid var(--g200)', cursor: page === 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, opacity: page === 1 ? 0.35 : 1 }}>1</button>
                      {page > 3 && <span style={{ fontSize: 11, color: 'var(--g400)', padding: '0 2px' }}>...</span>}
                      {Array.from({ length: 3 }, (_, i) => page - 1 + i).filter(p => p > 1 && p < totalPages).map(p => (
                        <button key={p} onClick={() => setPage(p)} style={{ minWidth: 28, height: 28, borderRadius: 6, border: '1px solid var(--g200)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontFamily: 'inherit', background: p === page ? 'var(--blue)' : 'var(--white)', color: p === page ? '#fff' : 'var(--g500)', borderColor: p === page ? 'var(--blue)' : 'var(--g200)' }}>
                          {p}
                        </button>
                      ))}
                      {page < totalPages - 2 && <span style={{ fontSize: 11, color: 'var(--g400)', padding: '0 2px' }}>...</span>}
                      {totalPages > 1 && (
                        <button onClick={() => setPage(totalPages)} style={{ minWidth: 28, height: 28, borderRadius: 6, border: '1px solid var(--g200)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontFamily: 'inherit', background: totalPages === page ? 'var(--blue)' : 'var(--white)', color: totalPages === page ? '#fff' : 'var(--g500)', borderColor: totalPages === page ? 'var(--blue)' : 'var(--g200)' }}>
                          {totalPages}
                        </button>
                      )}
                      <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={{ minWidth: 28, height: 28, borderRadius: 6, background: 'var(--white)', border: '1px solid var(--g200)', cursor: page === totalPages ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: page === totalPages ? 0.35 : 1 }}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
      {lightboxIndex !== null && <ImageLightbox images={imageUrls} currentIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />}
      {videoIndex !== null && cashVideos.length > 0 && <VideoLightbox videos={cashVideos} currentIndex={videoIndex} onClose={() => setVideoIndex(null)} />}
    </div>
  );
}
